import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import QRCode from "qrcode";
import { Crown, BookOpen, Video, Trophy, ScanLine } from "lucide-react";
import { ACADEMY_LOGO, ACADEMY_LOGO_H } from "../data/assets";

const GOLD = "#D4AF37", RED = "#C41E24", INK = "#111111";
const GOLD_GRAD = `linear-gradient(135deg, #F0D77A, ${GOLD} 60%, #B8912E)`;
const COPY = {
  club: {
    7: { head: ["See What Your Player", "Is Capable Of.", "7 Days Free."], campaign: "club-7day", chip: "7-Day Free Trial" },
    14: { head: ["Two Weeks", "to Prove It.", "14 Days Free."], campaign: "club-14day", chip: "14-Day Free Trial" },
    kicker: "Club Players · Coaches · Directors of Coaching",
    sub: "The region's first digital performance lab for serious club players.",
    bullets: [[BookOpen, "Personalized ball mastery curriculum"], [Video, "Video proof tracking with coach feedback"], [Trophy, "Live Central Illinois Rankings"]],
    path: "club",
  },
  school: {
    7: { head: ["Train With", "Purpose.", "7 Days Free."], campaign: "school-7day", chip: "7-Day Free Trial" },
    14: { head: ["Build Your", "Film.", "14 Days Free."], campaign: "school-14day", chip: "14-Day Free Trial" },
    kicker: "HS Coaches · Athletic Directors · College-Bound Players",
    sub: "The digital performance platform serious high school players use to stand out.",
    bullets: [[BookOpen, "Structured technical curriculum built for elite development"], [Video, "Video proof library — your player's progress, documented"], [Trophy, "Coach feedback on every rep submitted"]],
    path: "school",
  },
};
export const trialUrl = (days, audience = "club") => `https://hub.nextosoccer.com/trial/${COPY[audience].path}?utm_source=flyer&utm_medium=print&utm_campaign=${COPY[audience][days].campaign}`;

const useQr = (url) => {
  const [qr, setQr] = useState("");
  useEffect(() => { QRCode.toDataURL(url, { margin: 1, width: 900, errorCorrectionLevel: "H", color: { dark: "#0a0a0a", light: "#ffffff" } }).then(setQr); }, [url]);
  return qr;
};

// Shared flyer body. `social` = 1080×1350 portrait for Instagram/Facebook; otherwise 8.5×11 print.
const Flyer = ({ days, social, audience }) => {
  const a = COPY[audience] || COPY.club;
  const c = a[days] || a[7];
  const qr = useQr(trialUrl(days, audience));
  const W = social ? 1080 : 816, H = social ? 1350 : 1056, pad = social ? 64 : 56;
  const s = social ? 1.25 : 1;
  return (
    <section data-testid={`trial-flyer-${audience}-${days}${social ? "-social" : ""}`} className="relative overflow-hidden text-white print-page" style={{ width: W, height: H, background: INK }}>
      <div className="absolute inset-0 elite-mesh-overlay opacity-50 pointer-events-none" />
      <div className="absolute inset-x-0 top-0" style={{ height: 6, background: `linear-gradient(90deg, ${RED}, ${GOLD} 50%, ${RED})` }} />
      <div className="absolute -right-24 -top-24 rounded-full pointer-events-none" style={{ width: 460, height: 460, background: `radial-gradient(circle, ${GOLD}22, transparent 65%)` }} />
      <div className="relative h-full flex flex-col" style={{ padding: pad }}>
        <div className="flex items-start justify-between">
          <img src={ACADEMY_LOGO_H} alt="The Next O Technical Academy" style={{ height: 64 * s }} />
          <span className="inline-flex items-center gap-2 rounded-full border font-mono font-bold uppercase" style={{ borderColor: `${GOLD}99`, color: GOLD, background: `${GOLD}14`, fontSize: 11 * s, letterSpacing: "0.3em", padding: `${8 * s}px ${16 * s}px` }}><Crown size={12 * s} /> {c.chip}</span>
        </div>
        <div style={{ marginTop: 52 * s }}>
          <div className="font-mono font-bold uppercase" style={{ color: GOLD, fontSize: 12 * s, letterSpacing: "0.45em" }}>{a.kicker}</div>
          <h1 data-testid="trial-flyer-headline" className="font-cover uppercase leading-[0.88]" style={{ fontSize: 76 * s, marginTop: 14 * s }}>
            <span className="block" style={{ color: GOLD }}>{c.head[0]}</span>
            <span className="block" style={{ color: GOLD }}>{c.head[1]}</span>
            <span className="block text-white">{c.head[2]}</span>
          </h1>
          <p data-testid="trial-flyer-sub" className="text-white/80 leading-snug" style={{ fontSize: 20 * s, marginTop: 18 * s, maxWidth: 560 * s }}>{a.sub}</p>
        </div>
        <ul className="space-y-3" style={{ marginTop: 40 * s }}>
          {a.bullets.map(([Icon, t]) => (
            <li key={t} className="flex items-center gap-4">
              <span className="rounded-md flex items-center justify-center shrink-0 text-black" style={{ width: 40 * s, height: 40 * s, background: GOLD_GRAD }}><Icon size={18 * s} /></span>
              <span className="font-headings uppercase tracking-wide text-white leading-tight" style={{ fontSize: 21 * s }}>{t}</span>
            </li>
          ))}
        </ul>
        <div className="mt-auto flex items-end gap-8">
          <div className="rounded-xl bg-white shrink-0" style={{ padding: 10 * s, border: `3px solid ${GOLD}` }} data-testid="trial-flyer-qr">{qr && <img src={qr} alt="Scan to start your free trial" style={{ width: 190 * s, height: 190 * s, display: "block" }} />}</div>
          <div className="flex-1 min-w-0">
            <div className="inline-flex items-center gap-2 font-mono font-bold uppercase" style={{ color: "#ff8a8e", fontSize: 11 * s, letterSpacing: "0.35em" }}><ScanLine size={13 * s} /> Scan with your phone camera</div>
            <div className="font-headings uppercase tracking-wide leading-tight" style={{ fontSize: 28 * s, marginTop: 8 * s }}>Scan to start your free trial.</div>
            <div className="font-headings uppercase tracking-wide" style={{ fontSize: 28 * s, color: GOLD }}>No credit card required.</div>
            <div className="font-mono text-white/55" style={{ fontSize: 11 * s, marginTop: 12 * s, letterSpacing: "0.1em" }}>hub.nextosoccer.com/trial/{a.path}</div>
          </div>
          <img src={ACADEMY_LOGO} alt="" className="hidden sm:block" style={{ width: 110 * s, filter: `drop-shadow(0 0 18px ${GOLD}55)` }} />
        </div>
        <div className="flex items-center justify-between border-t font-mono uppercase text-white/50" style={{ borderColor: `${GOLD}44`, marginTop: 28 * s, paddingTop: 14 * s, fontSize: 11 * s, letterSpacing: "0.3em" }}>
          <span>hub.nextosoccer.com</span><span style={{ color: GOLD }}>The Next O Technical Academy</span><span>info@nextosoccer.com</span>
        </div>
      </div>
    </section>
  );
};

// /print/trial-flyer/:days (8.5×11 PDF) and /print/trial-flyer/:days/social (1080×1350 PNG), rendered by scripts/render_trial_flyers.py
export default function PrintTrialFlyer({ social = false }) {
  const { days, audience = "club" } = useParams();
  const d = Number(days) === 14 ? 14 : 7;
  const aud = COPY[audience] ? audience : "club";
  return (
    <div data-testid="trial-flyer-doc" className="bg-black" style={{ width: social ? 1080 : 816 }}>
      {!social && <style>{`@page { size: 8.5in 11in; margin: 0; }`}</style>}
      <Flyer days={d} social={social} audience={aud} />
    </div>
  );
}
